import { DateTime, IANAZone } from "luxon";
import { SiteDTO } from "src/app/api/api-types";
import {
  getPartnerApiUrl,
  getPartnerRouteUrl,
  Partner,
} from "src/app/partner/services/partners/partner.model";
import { parseDateTime, UUID } from "src/utils";
import { parseNumber } from "src/utils/converters/core-converters";

export class Site {
  public constructor(args: ClassProperties<Site>) {
    this.createdDate = args.createdDate;
    this.displayName = args.displayName;
    this.enableGuardPrompts = args.enableGuardPrompts;
    this.isManagedReceivingSite = args.isManagedReceivingSite;
    this.id = args.id;
    this.masterSiteId = args.masterSiteId;
    this.modifiedDate = args.modifiedDate;
    this.name = args.name;
    this.partner = args.partner;
    this.role = args.role;
    this.status = args.status;
    this.timeZone = args.timeZone;
  }

  public readonly createdDate: DateTime;
  public readonly displayName: string;
  public readonly enableGuardPrompts: boolean;
  public readonly isManagedReceivingSite: boolean;
  public readonly id: number;
  public readonly masterSiteId: number;
  public readonly modifiedDate: DateTime | null;
  public readonly name: string;
  public readonly partner: Partner;
  public readonly role: string;
  public readonly status: string;
  public readonly timeZone: IANAZone;

  /**
   * Parse the URL for partner and site info embedded in it.
   *
   * @param url - The URL to parse.
   */
  public static parseRouteInfo(url: string): {
    /**
     * The partner key from the URL, if present, or `"_"` if it's the "selected
     * partner" placeholder.
     */
    readonly partnerKey: UUID | "_" | null;
    /**
     * The site ID from the URL, if present, or `"_"` if it's the "selected
     * site" placeholder.
     */
    readonly siteId: number | "_" | null;
    /** The remaining part of the URL after the site path. */
    readonly childPath: string;
  } {
    const { partnerKey, childPath: partnerChildPath } =
      Partner.parseRouteInfo(url);
    const groups: UrlMatchGroups =
      partnerChildPath.match(urlSitePartMatch)?.groups ?? {};
    return {
      partnerKey,
      siteId: groups.id === "_" ? "_" : parseNumber(groups.id),
      childPath: groups.childPath ?? partnerChildPath,
    };
  }

  /**
   * Replaces the site (and partner, if necessary) portion of the provided URL
   * and returns the new URL for this site.
   *
   * @param url - The URL to replace the site path in.
   */
  public replaceRouted(url: string): string {
    return this.partner.replaceRouted(
      url.replace(urlSitePartMatch, `/sites/${this.id}$2`),
    );
  }

  public getRouteUrl(...childPaths: string[]): string {
    return getSiteRouteUrl(this, ...childPaths);
  }

  public getApiUrl(...childPaths: string[]): string {
    return getSiteApiUrl(this, ...childPaths);
  }
}

interface DeserializeArguments {
  partner: Partner;
}

export function deserializeSite(
  data: SiteDTO,
  { partner }: DeserializeArguments,
): Site {
  const timeZone = IANAZone.create(data.timeZone);
  return new Site({
    ...data,
    createdDate: parseDateTime(data.createdDate, { timeZone }),
    modifiedDate: data.modifiedDate
      ? parseDateTime(data.modifiedDate, {
          timeZone,
        })
      : null,
    partner,
    timeZone,
    isManagedReceivingSite: data.isManagedReceivingSite,
  });
}

export interface SiteReference {
  readonly id: number;
  readonly partner: Pick<Partner, "key">;
}

export interface SiteModelReference {
  readonly id: number;
  readonly site: SiteReference;
}

export function getSiteRouteUrl(
  site: SiteReference,
  ...childPaths: string[]
): string {
  return getPartnerRouteUrl(
    site.partner.key,
    "sites",
    String(site.id),
    ...childPaths,
  );
}

export function getSiteApiUrl(
  site: SiteReference,
  ...childPaths: string[]
): string {
  return getPartnerApiUrl(
    site.partner.key,
    "site",
    String(site.id),
    ...childPaths,
  );
}

const urlSitePartMatch = /\/sites\/(?<id>_|[0-9]+)(?<childPath>\/.*|$)/i;
interface UrlMatchGroups {
  readonly id?: string;
  readonly childPath?: string;
}
export enum FieldValidation {
  Disabled = 3,
  Optional = 2,
  Required = 1,
}
